import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { ServerSideGridComponent } from '@components/shared/grids/server-side-grid/server-side-grid.component';
import { BuildingService } from '@http/clients/building.service';
import { GridService } from '@services/grid.service';
import { AccountView, SearchBuildingsForAccountParameters } from '@services/http/clients/api-proxies';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ACCOUNT_BUILDINGS_GRID_COLUMNS } from './accounts-single-buildings.config';

@Component({
    selector: 'app-accounts-single-buildings',
    templateUrl: './accounts-single-buildings.component.html',
    styleUrls: ['./accounts-single-buildings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent]
})
export class AccountsSingleBuildingsComponent {
    account = input<AccountView>();

    tabCounterChange = output<void>();
    accountChange = output<void>();

    buildingsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns: ColDef[] = [...ACCOUNT_BUILDINGS_GRID_COLUMNS];

    private readonly buildingService = inject(BuildingService);
    private readonly gridService = inject(GridService);

    constructor() {
        effect(() => {
            const accountId = this.account()?.id;

            if (accountId && !this.buildingsDatasource) {
                this.createDatasource();
            }
        });
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private searchParamsBuilder(gridParams: any): SearchBuildingsForAccountParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const accountId = this.account()?.id;
        if (!accountId) {
            throw new Error('Account ID is required to search buildings.');
        }

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            accountId
        } as SearchBuildingsForAccountParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.buildingsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.buildingService.searchBuildingsForAccount(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching buildings:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
